import { computed, reactive, readonly, ref } from "vue";
import { propositionReponse } from "@/api/helpdesk";

/**
 * La proposition de réponse de Luce, partagée entre le panneau et la fiche ticket.
 *
 * Les deux ne se connaissent pas : le panneau vit dans le layout et survit aux changements de
 * route, la zone de réponse vit dans la fiche. L'état est donc tenu ici, au niveau du module, et
 * non dans un store : une seule donnée, un seul écran qui la produit.
 *
 * Luce ne peut pas envoyer : elle copie le texte dans la zone de réponse de la fiche ouverte, et
 * c'est l'agent qui clique sur Envoyer.
 */
const etat = reactive({
  ticketId: null,
  reference: null,
  texte: null,
  motif: null,
  raison: null,
  chargement: false,
  // Incrémenté à chaque proposition prête : le panneau s'ouvre sur ce signal.
  ouvertures: 0,
});

// La zone de réponse actuellement affichée, et le ticket qu'elle sert. Copier dans celle d'un
// autre ticket enverrait au mauvais client une réponse écrite pour un autre.
const cible = { ticketId: null, recevoir: null };
const cibleTicketId = ref(null);

// Chaque demande porte un numéro : la réponse d'un ticket quitté entre-temps est ignorée.
let derniere = 0;

async function proposer(ticketId, { reference = null, indication = null, regenerer = false } = {}) {
  const numero = ++derniere;
  etat.ticketId = ticketId;
  etat.reference = reference;
  etat.chargement = true;
  if (regenerer) etat.texte = null;

  try {
    const res = await propositionReponse(ticketId, {
      indication: indication || undefined,
      regenerer: regenerer ? 1 : undefined,
    });
    if (numero !== derniere) return;

    const proposition = res.data || {};
    etat.texte = proposition.texte || null;
    etat.motif = proposition.motif || null;
    etat.raison = proposition.raison || null;

    // Le client n'attend rien : ouvrir Luce ne servirait qu'à déranger.
    if (etat.motif !== "sans_attente") etat.ouvertures++;
  } catch (err) {
    if (numero !== derniere) return;
    etat.texte = null;
    etat.motif = "erreur";
    etat.raison = err?.response?.status === 429
      ? "Trop de propositions demandées en peu de temps. Réessayez dans une minute."
      : "La proposition n'a pas pu être obtenue.";
    // Une régénération demandée depuis le panneau doit dire son échec ; une ouverture de fiche,
    // non : l'agent n'a rien demandé.
    if (regenerer) etat.ouvertures++;
  } finally {
    if (numero === derniere) etat.chargement = false;
  }
}

/** Une réponse vient de partir, ou la fiche change de ticket : la proposition est périmée. */
function oublier(ticketId) {
  if (etat.ticketId !== ticketId) return;
  derniere++;
  Object.assign(etat, { ticketId: null, reference: null, texte: null, motif: null, raison: null, chargement: false });
}

function enregistrerCible(ticketId, recevoir) {
  cible.ticketId = ticketId;
  cible.recevoir = recevoir;
  cibleTicketId.value = ticketId;
}

function retirerCible(ticketId) {
  if (cible.ticketId !== ticketId) return;
  cible.ticketId = null;
  cible.recevoir = null;
  cibleTicketId.value = null;
}

const peutCopier = computed(() => Boolean(etat.texte) && etat.ticketId !== null && cibleTicketId.value === etat.ticketId);

function copier() {
  if (!peutCopier.value) return false;
  cible.recevoir(etat.texte);
  return true;
}

export function useLuceProposition() {
  return {
    proposition: readonly(etat),
    peutCopier,
    proposer,
    oublier,
    enregistrerCible,
    retirerCible,
    copier,
  };
}
